import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, TextField, Button, Card, CardContent, Typography, Avatar, Stack, Paper, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import { updateJobNotes } from "../../redux_store/jobState";
import DOMPurify from "dompurify";
import ReactToPrint from "react-to-print";
import { LocalPrintshop } from "@material-ui/icons";

const dateTimeOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
};

const canadaGibsonFont = {
    fontFamily: "canada-type-gibson",
    "& .MuiInputBase-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiInputLabel-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiFormHelperText-root": {
        fontFamily: "canada-type-gibson",
    },
    "& .MuiMenuItem-root": {
        fontFamily: "canada-type-gibson",
    },
};

function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
}

function stringAvatar(name) {
    return {
        sx: {
            bgcolor: stringToColor(name),
        },
        children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
}

export function JobNotes({ toggleOpen }) {
    const dispatch = useDispatch();
    const jobNotes = useSelector((state) => state.job.JobNotes);
    const user = useSelector((state) => state.app.user);
    const [input, setInput] = useState("");
    const printRef = useRef();

    const queryParams = new URLSearchParams(window.location.search);
    const jobNumber = queryParams.get("job");

    const handleAddComment = (e) => {
        e.preventDefault();
        const trimmed = input.trim();
        if (!trimmed) return;

        const sanitized = DOMPurify.sanitize(trimmed, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: [],
        });

        dispatch(
            updateJobNotes({
                timestamp: new Date().toISOString(),
                userId: user.id,
                userCode: user.UserCode,
                userName: user.DisplayName,
                text: sanitized,
            })
        );

        setInput("");
    };

    const handleClose = () => {
        toggleOpen(false);
    };

    return (
        <>
            <div style={{ display: "none" }}>
                <div
                    ref={printRef}
                    style={{
                        padding: "20mm",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "12pt",
                        lineHeight: 1.5,
                    }}
                >
                    <Typography variant="h6" gutterBottom sx={{ ...canadaGibsonFont }}>
                        JOB NOTES - {jobNumber}
                    </Typography>
                    {[...(jobNotes || [])].reverse().map((comment) => {
                        const localDateString = new Date(comment.timestamp).toLocaleString(undefined, dateTimeOptions);
                        return (
                            <Box key={comment.timestamp} sx={{ mb: 2 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ ...canadaGibsonFont }}
                                >{`${comment.userName} (${comment.userId})`}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ...canadaGibsonFont }}>
                                    {localDateString}
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 1, ...canadaGibsonFont }}>
                                    {comment.text}
                                </Typography>
                                <hr />
                            </Box>
                        );
                    })}
                </div>
            </div>
            <Box
                sx={{
                    position: "absolute",
                    top: 100,
                    left: 100,
                    zIndex: 1400,
                }}
            >
                <Draggable handle=".drag-handle" cancel="input,textarea,button">
                    <div>
                        <ResizableBox
                            width={600}
                            height={500}
                            minConstraints={[600, 200]}
                            maxConstraints={[600, 1000]}
                            resizeHandles={["se"]}
                        >
                            <Paper
                                className="drag-handle"
                                elevation={6}
                                sx={{
                                    cursor: "move",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                    padding: 2,
                                }}
                            >
                                <Box sx={{ flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ ...canadaGibsonFont }}>
                                            JOB NOTES
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <ReactToPrint
                                                trigger={() => {
                                                    return (
                                                        <Button variant="outlined">
                                                            <LocalPrintshop />
                                                        </Button>
                                                    );
                                                }}
                                                content={() => printRef.current}
                                            />
                                            <IconButton onClick={handleClose}>
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ mb: 2, ...canadaGibsonFont }}
                                    >
                                        Notes are visible to BBC users only.
                                    </Typography>

                                    <form onSubmit={handleAddComment}>
                                        <TextField
                                            label="Add a note..."
                                            placeholder="Max 1000 characters"
                                            inputProps={{ maxLength: 1000 }}
                                            multiline
                                            fullWidth
                                            value={input}
                                            maxRows={10}
                                            onChange={(e) => setInput(e.target.value)}
                                            sx={{ mb: 1, ...canadaGibsonFont }}
                                        />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                gap: 1,
                                                ...canadaGibsonFont,
                                            }}
                                        >
                                            <Button onClick={handleClose}>Cancel</Button>
                                            <Button type="submit" variant="contained" disabled={!input.trim()}>
                                                Post
                                            </Button>
                                        </Box>
                                    </form>
                                </Box>

                                <Box
                                    sx={{
                                        mt: 2,
                                        overflowY: "auto",
                                        flexGrow: 1,
                                        paddingRight: 1,
                                        paddingTop: 1,
                                        paddingBottom: 1,
                                    }}
                                >
                                    <Stack spacing={2}>
                                        {[...(jobNotes || [])].reverse().map((comment) => {
                                            const localDateString = new Date(comment.timestamp).toLocaleString(
                                                undefined,
                                                dateTimeOptions
                                            );
                                            return (
                                                <Card key={comment.timestamp}>
                                                    <CardContent>
                                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                                            <Avatar {...stringAvatar(comment.userName)} />
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    alignItems: "flex-start",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{ ...canadaGibsonFont }}
                                                                >
                                                                    {`${comment.userName} (${comment.userId})`}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ ...canadaGibsonFont }}
                                                                >
                                                                    {localDateString}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        mt: 1,
                                                                        textAlign: "left",
                                                                        fontWeight: 200,
                                                                        ...canadaGibsonFont,
                                                                    }}
                                                                >
                                                                    {comment.text}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </Paper>
                        </ResizableBox>
                    </div>
                </Draggable>
            </Box>
        </>
    );
}
